import { useDebounce } from "@lyfegen-platform/react-common";
import { Box, Typography, useTheme } from "@material-ui/core";
import { useFieldValue } from "formik";
import { ChangeEvent, useEffect, useReducer } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import LAComplete from "../components/LAComplete";
import useStyles from "../containers/data/forms/forms-styles";
import { useLazyDataQuery } from "../hooks/useDataModule";
import { EMPTY_VALUE_PLACEHOLDER } from "../models/common/data";
import { NON_OPTION_VALUE } from "../models/common/options";
import { FlexFieldController } from "../models/flexible/flex-field-controller";
import { isEmptyValue } from "../utils/is-empty-value";
import { GeneralFieldController } from "./general-field-controller";
import { GET_GENERICS_BY_NAME } from "./queries";
import { GetGenericsByName } from "./types/GetGenericsByName";

interface GenericState {
	typedValue: string | null;
	options: Array<string | null> | null;
}

enum GenericReducerActionsTypes {
	"setTypedValue",
	"setOptions",
}

type GenericReducerActions =
	| { type: GenericReducerActionsTypes.setTypedValue; payload: any }
	| { type: GenericReducerActionsTypes.setOptions; payload: any };

const reducer = (state: GenericState, action: GenericReducerActions): GenericState => {
	switch (action.type) {
		case GenericReducerActionsTypes.setOptions:
			return { ...state, options: action.payload };
		case GenericReducerActionsTypes.setTypedValue:
			return { ...state, typedValue: action.payload };
		default:
			return { ...state };
	}
};

const initialState: GenericState = {
	typedValue: null,
	options: null,
};

const getInitialValue = (controller: FlexFieldController, path: string) => {
	const properties = path.split(".");
	if (!controller.parent.context.flexEntity) return undefined;
	const data = controller.parent.context.flexEntity.data;
	return properties.reduce((previous, current) => previous[current], data);
};

function RenderGenericByNameSelector(props: { controller: FlexFieldController; path: string }) {
	const { t } = useTranslation();
	const styles = useStyles();
	const { palette } = useTheme();
	const value = useFieldValue(props.path);
	const [{ typedValue, options }, dispatchGenericState] = useReducer(reducer, initialState);
	const debouncedInput = useDebounce(typedValue, 200);
	const initialValue: string | undefined = getInitialValue(props.controller, props.path);
	const formattedInitialValue = initialValue && { label: initialValue, value: initialValue };
	const nonOption = { label: t(NON_OPTION_VALUE), value: NON_OPTION_VALUE };
	const title = props.controller.parent ? props.controller.path.replace(`${props.controller.parent.path}.`, "") : props.controller.path;

	const [fetch, { loading, error }] = useLazyDataQuery<GetGenericsByName>(GET_GENERICS_BY_NAME, {
		onCompleted: (data) =>
			dispatchGenericState({
				type: GenericReducerActionsTypes.setOptions,
				payload: [nonOption, ...data.generics.map((generic) => ({ label: generic.Name, value: generic.Name }))],
			}),
	});

	useEffect(() => {
		if (debouncedInput) {
			fetch({ variables: { name: debouncedInput } });
		}
	}, [debouncedInput]);

	if (!props.controller.parent.context.isEditable) {
		return (
			<Box className={styles.colMedium} pr={5} pl={1.5} paddingY={2.5}>
				<Typography variant="caption" color="textSecondary">
					{t(props.controller.schema.attribute.shortLabelLocalizationKey)}
				</Typography>
				<Typography style={{ color: palette.text.disabled }}>{isEmptyValue(value) ? EMPTY_VALUE_PLACEHOLDER : value}</Typography>
			</Box>
		);
	}

	const handleInputChange = (e: ChangeEvent<{}>) => {
		const input = (e?.target as any)?.value;
		if (input) {
			dispatchGenericState({ type: GenericReducerActionsTypes.setTypedValue, payload: input });
		}
	};

	if (error) {
		return <>{console.error(error)}</>;
	}

	return (
		<Box className={styles.colMedium} pr={5} paddingY={2.5}>
			<LAComplete
				title={t(title)}
				name={props.path}
				property="value"
				options={!options && initialValue ? [formattedInitialValue] : options || []}
				loading={loading}
				onInput={handleInputChange}
				getOptionLabel={(v: any) => v?.label}
				selectPropertyValue
			/>
		</Box>
	);
}

export class PreviousTherapyFieldController extends GeneralFieldController {
	customRenderContent(path: string): React.ReactElement {
		return <RenderGenericByNameSelector controller={this} path={path} />;
	}

	override createValidationRule(initialValues: undefined, translate: (key: string) => string): Yup.AnySchema {
		return Yup.string().required();
	}
}
